import { collection, doc } from 'firebase/firestore'
import { Book } from './Book'
import { Sheet } from './Sheet'
import { Page } from './Page'
import { CoverFactory, COVER_TYPE_TEMPLATE } from './cover'
import { escapeHtml } from '@/utils/escapeHtml'
import { formatIsbn } from '@/utils/formatIsbn'
// import { PrintOptions } from './PrintOptions'

class Project extends Book {
  bookName
  bookNameKana
  description
  genreId
  adultLevel
  cover
  coverPages
  canDelete
  pageLimit
  authors
  authorEn
  template
  salesStart
  isbn
  jan
  publisherId
  publisherName
  #paperColor

  constructor (ref, data, sheets) {
    super(ref, sheets)

    this.bookName = data.bookName || data.name || '' // name is depricated
    this.bookNameKana = data.bookNameKana || ''
    this.description = data.description || data.intro || '' // intro is depricated
    this.genreId = data.genreId || ''
    this.adultLevel = data.adultLevel ?? -1
    this.authors = data.authors || []
    this.authorEn = data.authorEn || ''
    this.template = data.template
    this.cover = data.cover || {}
    // Страницы шаблонной обложки: { back: Page, variants: { [coverId]: Page } }.
    // Живут вне sheets, поэтому не влияют на нумерацию, лимит страниц и расчёт корешка.
    this.coverPages = data.coverPages || { back: null, variants: {} }
    // Шаблон обложки (CoverTemplate), подобранный по формату книги.
    this.coverTemplate = data.coverTemplate || null
    this.coverType = data.coverType || 'MATTE'
    this.canDelete = data.canDelete ?? true
    this.pageLimit = data.pageLimit ?? 10

    this.salesStart = data.salesStart?.toDate() || new Date()
    this.retailPrice = data.retailPrice || 0
    this.isbn = data.isbn || ''
    this.jan = data.jan || []
    this.keywords = data.keywords || []
    this.publisherId = data.publisherId || '0097'
    this.publisherName = data.publisherName || ''
    this.paperColor = data.paperColor || 'WHI'
  }

  /* Обёртка над plain-данными this.cover: ImageCover или TemplateCover. */
  get coverModel () {
    return CoverFactory.create(this)
  }

  /* Режим обложки с учётом дефолтов (см. CoverFactory.resolveType). */
  get isTemplateCover () {
    return this.coverModel.type === COVER_TYPE_TEMPLATE
  }

  /* Лист обложки для воркспейса; null, если обложка не шаблонная. */
  get coverSheet () {
    return this.coverModel.createSheet()
  }

  get hasCoverTemplates () {
    return (this.coverTemplate?.variants?.length || 0) > 0
  }

  get spineTitle () {
    return this.cover?.spineTitle || this.bookName
  }

  get spineFontSize () {
    return this.cover?.spineFontSize ?? null
  }

  get coverSpineWidthInMm () {
    const colorPageThickness = 0.0596
    const bwWhitePaperThickness = 0.0572
    const bwCreeamPaperThickness = 0.0635
    const bwPageThickness = this.colorPagesNumber > 0
      ? bwWhitePaperThickness
      : (this.paperColor === 'WHI'
          ? bwWhitePaperThickness
          : bwCreeamPaperThickness
        )

    return +(this.colorPagesNumber * colorPageThickness + this.bwPagesNumber * bwPageThickness).toFixed(2)
  }

  get colorPagesNumber () {
    return this.pages.filter((page) => page.colorPrint).length
  }

  get bwPagesNumber () {
    return this.pages.filter((page) => !page.colorPrint).length
  }

  get paperColor () {
    return this.colorPagesNumber > 0 ? 'WHI' : this.#paperColor
  }

  set paperColor (value) {
    if (!['WHI', 'CRE'].includes(value)) {
      throw new Error('Only "WHI" and "CRE" values are allowed')
    }

    this.#paperColor = value
  }

  toObject () {
    const data = super.toObject()
    // data.printOptions = data.printOptions.toObject()
    data.template = data.template._ref
    data.paperColor = this.#paperColor
    delete data.sheets
    delete data.pageLimit
    // Страницы обложки сохраняются отдельными документами; в проекте лежат только ссылки
    // на них внутри data.cover. Шаблон обложки подбирается по формату и не сериализуется.
    delete data.coverPages
    delete data.coverTemplate
    return data
  }

  // getPrintInfo () {
  //   const result = {
  //     isbn: this.isbn || '',
  //     publisherId: this.publisherId || '',
  //     publisherName: this.publisherName,
  //     bookName: this.bookName,
  //     bookNameKana: this.bookNameKana,
  //     code: this.code,
  //     genreId: this.genreId,
  //     issueForm: '書籍',
  //     adultLevel: this.adultLevel,

  //   }

  //   return result
  // }

  isReadyToPrint () {
    return this.bookName && this.bookNameKana &&
        Boolean(this.description) && this.description.length >= 200 && this.description.length <= 1000 &&
        this.coverModel.isComplete() &&
        this.authors.length > 0
        // this.genreId &&
        // this.adultLevel >= 0 &&
        // this.publisherName &&
        // this.isbn &&
        // this.keywords.length > 0 &&
        // this.salesStart >= new Date(new Date() * 1 + 1209600000) &&
        // this.retailPrice >= 100
  }

  createSheet (pageTemplates) {
    const ref = this.#genRef('sheets')
    const pages = pageTemplates
      .filter((pageTemplate) => Boolean(pageTemplate))
      .map((pageTemplate) => this.createPage(pageTemplate))
    const order = this.#genSheetOrder(pages)
    const format = this.template.printInfo.format
    return new Sheet(ref, { pages, order, format })
  }

  createPage (pageTemplate) {
    const ref = this.#genRef('pages')
    const data = JSON.parse(pageTemplate.toJSON())

    data.template = pageTemplate._ref

    return new Page(ref, data)
  }

  getJanCode () {
    if (this.jan.length === 3 && Number(this.retailPrice) > 100) {
      const partA = '192'
      const partB = `${this.jan[0]}${this.jan[1]}${this.jan[2]}`
      const partC = String(this.retailPrice).padStart(5, '0')
      const partsABC = `${partA}${partB}${partC}`
      const partD = this.#calculateJanCheckDigit(partsABC)
      const janCode = `${partsABC}${partD}`

      return janCode
    } else {
      return undefined
    }
  }

  #calculateJanCheckDigit (janCode) {
    if (janCode.length !== 12) {
      throw new Error('The JAN code must contain exactly 12 digits.')
    }

    const digits = janCode.split('').map(Number)

    if (digits.some(isNaN)) {
      throw new Error('The JAN code must contain only numbers.')
    }

    const oddSum = digits.reduce((sum, digit, index) => {
      return index % 2 === 0 ? sum + digit : sum
    }, 0)

    const evenSum = digits.reduce((sum, digit, index) => {
      return index % 2 !== 0 ? sum + digit : sum
    }, 0) * 3

    const total = oddSum + evenSum
    const checkDigit = (10 - (total % 10)) % 10

    return checkDigit
  }

  #genRef (collectionName) {
    let ref

    do {
      ref = doc(collection(this._ref, collectionName))
    } while (ref.id[0] >= '0' && ref.id[0] <= '9')

    return ref
  }

  #genSheetOrder (pages) {
    if (pages.length > 1) {
      return this.middleSheets.length + 1
    } else {
      return pages[0].side === 'right' ? 0 : 9999
    }
  }

  syncColophonFields (options = {}) {
    const colophonPages = (this.pages || []).filter((page) => page.name === '奥付')
    if (colophonPages.length === 0) {
      return []
    }

    const modifiedPages = []

    for (const page of colophonPages) {
      let isModified = false
      if (!page.fields) {
        page.fields = {}
      }

      // 1. Book Title
      if (this.bookName && this.bookName.trim()) {
        const escapedTitle = escapeHtml(this.bookName.trim())
        const titleHtml = `<p>${escapedTitle}</p>`
        if (page.fields.title !== titleHtml) {
          page.fields.title = titleHtml
          isModified = true
        }
      }

      // 2. Authors
      if (this.authors && this.authors.length > 0) {
        const authorParagraphs = this.authors
          .filter((a) => a.fullname && a.fullname.trim())
          .map((a) => `<p>${escapeHtml(a.fullname.trim())}</p>`)
          .join('')

        if (authorParagraphs) {
          if (page.fields['author-name'] !== authorParagraphs) {
            page.fields['author-name'] = authorParagraphs
            isModified = true
          }
          if (page.fields.author !== authorParagraphs) {
            page.fields.author = authorParagraphs
            isModified = true
          }
        }
      }

      // Publisher (発行者)
      let publisherHtml = ''
      if (this.publisherName && this.publisherName.trim()) {
        publisherHtml = `<p>${escapeHtml(this.publisherName.trim())}</p>`
      } else if (this.authors && this.authors.length > 0) {
        const authorParagraphs = this.authors
          .filter((a) => a.fullname && a.fullname.trim())
          .map((a) => `<p>${escapeHtml(a.fullname.trim())}</p>`)
          .join('')
        if (authorParagraphs) {
          publisherHtml = authorParagraphs
        }
      }

      if (publisherHtml) {
        if (page.fields['publisher-name'] !== publisherHtml) {
          page.fields['publisher-name'] = publisherHtml
          isModified = true
        }
        if (page.fields.publisher !== publisherHtml) {
          page.fields.publisher = publisherHtml
          isModified = true
        }
      }

      // 3. Copyright (Year & English Author Name)
      const year = this.salesStart ? new Date(this.salesStart).getFullYear() : new Date().getFullYear()
      const mainAuthor = this.authors?.find((a) => a.role === '著') || this.authors?.[0]
      const authorEnName = this.authorEn?.trim() || mainAuthor?.fullname?.trim() || ''

      if (authorEnName) {
        const copyrightText = `${year} ${escapeHtml(authorEnName)}`
        if (page.fields.copyright !== copyrightText) {
          page.fields.copyright = copyrightText
          isModified = true
        }
      }

      // 4. ISBN
      const rawIsbn = options.resolvedIsbn || this.isbn || ''
      if (rawIsbn && typeof rawIsbn === 'string' && rawIsbn.trim() && !rawIsbn.includes('*')) {
        const formattedIsbn = formatIsbn(rawIsbn)
        const layoutSource = page.layout?.source || page.template?.layout?.source || ''
        const hasHardcodedPrefix = /ISBN978-4-911-<\s*span/i.test(layoutSource)

        let isbnFieldValue = ''
        if (hasHardcodedPrefix) {
          isbnFieldValue = formattedIsbn.replace(/^(ISBN)?(978-4-911-?|9784911)?/i, '')
        } else {
          isbnFieldValue = formattedIsbn
        }

        if (page.fields.isbn !== isbnFieldValue) {
          page.fields.isbn = isbnFieldValue
          isModified = true
        }
      }

      // 5. Release Date (start-date)
      if (this.salesStart) {
        const d = new Date(this.salesStart)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const dateStr = `${yyyy}年${mm}月${dd}日`
        if (page.fields['start-date'] !== dateStr) {
          page.fields['start-date'] = dateStr
          isModified = true
        }
      }

      if (isModified) {
        modifiedPages.push(page)
      }
    }

    return modifiedPages
  }
}

export { Project }
